import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Lock, CreditCard, FileCheck2, ShieldCheck, Cloud } from "lucide-react";
import { PLANS } from "../constants";

export const PricingSection = ({ compact = false }) => {
  const [yearly, setYearly] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-center gap-3 mb-10">
        <span className={`text-sm ${!yearly ? "text-white" : "text-slate-500"}`}>Monthly</span>
        <button
          data-testid="billing-toggle"
          onClick={() => setYearly((v) => !v)}
          className="relative h-6 w-11 rounded-full cc-surface-2 border border-white/10"
          aria-label="Toggle billing period"
        >
          <motion.span
            className="absolute top-0.5 h-5 w-5 rounded-full cc-gradient"
            animate={{ left: yearly ? "22px" : "2px" }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          />
        </button>
        <span className={`text-sm ${yearly ? "text-white" : "text-slate-500"}`}>
          Yearly <span className="text-emerald-400 text-xs">(save 20%)</span>
        </span>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
        {PLANS.map((p, i) => {
          const price = p.price === null ? null : yearly ? Math.round(p.price * 12 * 0.8) : p.price;
          const suffix = p.price === null ? "" : yearly ? "/yr" : "/mo";
          return (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`relative cc-card rounded-2xl p-6 flex flex-col ${p.popular ? "ring-1 ring-blue-500/50" : ""}`}
              data-testid={`plan-${p.name.toLowerCase()}`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full cc-gradient px-3 py-1 text-[11px] font-500 text-white">
                  Most Popular
                </span>
              )}
              <h3 className="cc-heading text-lg font-600 text-white">{p.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                {price === null ? (
                  <span className="cc-heading text-3xl font-700 text-white">Custom</span>
                ) : (
                  <>
                    <span className="cc-heading text-4xl font-700 text-white">${price}</span>
                    <span className="text-slate-500 text-sm mb-1.5">{suffix}</span>
                  </>
                )}
              </div>
              {p.to.startsWith("mailto") ? (
                <a href={p.to} className="mt-5 rounded-xl border border-white/12 py-2.5 text-center text-sm text-white cc-nav-item" data-testid={`plan-cta-${p.name.toLowerCase()}`}>
                  {p.cta}
                </a>
              ) : (
                <span className="mt-5 inline-flex items-center justify-center gap-1.5 rounded-xl border border-amber-300/25 bg-amber-400/10 py-2.5 text-center text-xs font-500 uppercase tracking-[0.14em] text-amber-200" data-testid={`plan-cta-${p.name.toLowerCase()}`}>
                  Coming Soon
                </span>
              )}
              <ul className="mt-6 space-y-2.5">
                {(compact ? p.features.slice(0, 6) : p.features).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check size={15} className="mt-0.5 shrink-0 text-blue-400" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3" data-testid="pricing-trust-badges">
        {[
          { icon: Lock, label: "256-bit Encryption" },
          { icon: CreditCard, label: "Stripe Secure Payments" },
          { icon: FileCheck2, label: "Cancel Anytime" },
          { icon: ShieldCheck, label: "Privacy Protected" },
          { icon: Cloud, label: "Secure Cloud Storage" },
        ].map(({ icon: Icon, label }) => (
          <span key={label} className="inline-flex items-center gap-1.5 text-xs text-slate-400">
            <Icon size={13} className="text-emerald-300" /> {label}
          </span>
        ))}
      </div>
      <p className="mt-5 text-center text-xs text-slate-500" data-testid="pricing-refund-note">
        Cancel anytime — you keep full access until the end of your billing period. No partial refunds.{" "}
        <a href="/refund-policy" className="text-blue-300 hover:text-blue-200 underline underline-offset-2">Refund Policy</a>
      </p>
    </div>
  );
};
